import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import { useUserPreferencesStore } from "./userPreferencesStore";
import { useUserPreferences } from "./useUserPreferences";
import { cn } from "@/lib/utils";

type TabType = "nudge-settings" | "github-access-token";

export const UserPreferencesModal = () => {
  const { isModalOpen, closeModal } = useUserPreferencesStore();
  const {
    smallNudge,
    largeNudge,
    setSmallNudge,
    setLargeNudge,
    githubAccessToken,
    setGithubAccessToken,
  } = useUserPreferences();

  const [activeTab, setActiveTab] = useState<TabType>("nudge-settings");

  const tabs = [
    { id: "nudge-settings" as TabType, label: "Nudge Settings" },
    { id: "github-access-token" as TabType, label: "Github Access Token" },
  ];

  return (
    <Dialog open={isModalOpen} onOpenChange={closeModal}>
      <DialogContent className="max-w-[600px] h-[400px] p-0">
        <div className="flex h-full">
          {/* Left Sidebar Navigation */}
          <div className="w-48 border-r border-border bg-background-secondary p-4">
            <h2 className="text-sm font-medium mb-4 text-text">Preferences</h2>
            <nav className="flex flex-col gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "text-xs text-left px-3 py-2 rounded-md transition-colors",
                    activeTab === tab.id
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-background-tertiary text-text-muted hover:text-text",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 p-6">
            {activeTab === "nudge-settings" && (
              <div className="flex flex-col gap-4">
                <h3 className="text-sm font-medium">Nudge Amount</h3>
                <div className="flex flex-col gap-4">
                  <NumberInput
                    id="small-nudge"
                    label="Small nudge"
                    value={smallNudge}
                    onChange={setSmallNudge}
                    min={1}
                    max={100}
                  />
                  <NumberInput
                    id="large-nudge"
                    label="Large nudge"
                    value={largeNudge}
                    onChange={setLargeNudge}
                    min={1}
                    max={100}
                  />
                </div>
              </div>
            )}

            {activeTab === "github-access-token" && (
              <div className="flex flex-col gap-4">
                <h3 className="text-sm font-medium">Github Access Token</h3>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="github-token" className="text-xs">
                    Access Token
                  </Label>
                  <Input
                    id="github-token"
                    type="text"
                    value={githubAccessToken}
                    onChange={(e) => setGithubAccessToken(e.target.value)}
                    placeholder="ghp_..."
                    className="h-8 text-xs"
                  />
                  <p className="text-xs text-text-muted mt-1">
                    <a
                      href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens#creating-a-personal-access-token-classic"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 underline"
                    >
                      Click here
                    </a>{" "}
                    to learn how to create a classic personal access token. Make
                    sure to select all repo permissions and no expiration.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
